import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, FriendResponse, FriendRequestResponse, SentFriendRequestResponse, UserSearchResult } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

// Query keys
export const friendKeys = {
  all: ['friends'] as const,
  requests: ['friend-requests'] as const,
  sent: ['friend-requests-sent'] as const,
  search: (query: string) => ['user-search', query] as const,
};

/**
 * Fetch the current user's friends list
 */
export function useFriends() {
  return useQuery<FriendResponse[]>({
    queryKey: friendKeys.all,
    queryFn: () => api.getFriends(),
  });
}

/**
 * Fetch pending incoming friend requests
 */
export function usePendingRequests() {
  return useQuery<FriendRequestResponse[]>({
    queryKey: friendKeys.requests,
    queryFn: () => api.getFriendRequests(),
  });
}

/**
 * Fetch sent (outgoing) friend requests
 */
export function useSentRequests() {
  return useQuery<SentFriendRequestResponse[]>({
    queryKey: friendKeys.sent,
    queryFn: () => api.getSentFriendRequests(),
  });
}

/**
 * Send a friend request to another user
 */
export function useSendFriendRequest() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (addresseeId: string) => api.sendFriendRequest(addresseeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: friendKeys.all });
      queryClient.invalidateQueries({ queryKey: friendKeys.requests });
      queryClient.invalidateQueries({ queryKey: friendKeys.sent });
      // Invalidate all search results so the button updates
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'user-search' });
      toast({ title: 'Friend request sent' });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to send request', description: err.message, variant: 'destructive' });
    },
  });
}

/**
 * Accept an incoming friend request
 */
export function useAcceptFriendRequest() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (friendshipId: string) => api.acceptFriendRequest(friendshipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: friendKeys.all });
      queryClient.invalidateQueries({ queryKey: friendKeys.requests });
      toast({ title: 'Friend request accepted' });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to accept', description: err.message, variant: 'destructive' });
    },
  });
}

/**
 * Decline an incoming friend request
 */
export function useDeclineFriendRequest() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (friendshipId: string) => api.declineFriendRequest(friendshipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: friendKeys.requests });
      toast({ title: 'Friend request declined' });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to decline', description: err.message, variant: 'destructive' });
    },
  });
}

/**
 * Cancel a sent friend request
 */
export function useCancelFriendRequest() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (friendshipId: string) => api.cancelFriendRequest(friendshipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: friendKeys.sent });
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'user-search' });
      toast({ title: 'Friend request cancelled' });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to cancel', description: err.message, variant: 'destructive' });
    },
  });
}

/**
 * Remove an existing friend
 */
export function useRemoveFriend() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (friendshipId: string) => api.removeFriend(friendshipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: friendKeys.all });
      toast({ title: 'Friend removed' });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to remove', description: err.message, variant: 'destructive' });
    },
  });
}

/**
 * Search users by query string (enabled when query >= 2 chars)
 */
export function useSearchUsers(query: string) {
  return useQuery<UserSearchResult[]>({
    queryKey: friendKeys.search(query),
    queryFn: () => api.searchUsers(query),
    enabled: query.length >= 2,
  });
}
